import { CaretDownIcon, SlidersHorizontalIcon } from '@phosphor-icons/react';
import { css, html } from 'react-strict-dom';
import { DropdownMenu, DropdownMenuItem, Stack, Text } from 'src/components';
import { Color, Style } from 'src/constants';
import { color } from 'src/constants/color.css';

type Props = {
  category: string;
  options: { id: string; name: string }[];
  selectedIds: string[];
  onToggle: (id: string) => void;
};

export default function FilterChip({
  category,
  options,
  selectedIds,
  onToggle,
}: Props) {
  const selectedNames = selectedIds
    .map((id) => options.find((o) => o.id === id)?.name)
    .filter(Boolean);
  const displayValue =
    selectedNames.length === 0 ? null : selectedNames.join(', ');

  return (
    <DropdownMenu
      trigger={(open) => (
        <html.button
          style={[
            Style.row,
            Style.alignCenter,
            styles.chip,
            open && styles.chipOpen,
          ]}
        >
          <Stack style={[Style.row, Style.alignCenter]} gap={0.5}>
            <SlidersHorizontalIcon size={20} />
            <Stack
              style={!displayValue && Style.row}
              gap={!displayValue ? 0.5 : 0}
            >
              <Text
                typeface={
                  !displayValue ? 'body.small.medium' : 'caption.medium'
                }
                color="neutral80"
                textAlign="left"
                style={!displayValue && styles.categoryContainerNoValue}
              >
                {category}
              </Text>

              <html.span style={styles.valueContainer}>
                <Text
                  typeface="body.regular"
                  color={displayValue ? 'neutral100' : 'neutral60'}
                >
                  {displayValue ?? 'Select'}
                </Text>
              </html.span>
            </Stack>
            <CaretDownIcon size={16} color={Color.neutral60} />
          </Stack>
        </html.button>
      )}
    >
      {options.map((option) => (
        <DropdownMenuItem
          key={option.id}
          selected={selectedIds.includes(option.id)}
          onClick={() => onToggle(option.id)}
          showRadio={false}
        >
          {option.name}
        </DropdownMenuItem>
      ))}
    </DropdownMenu>
  );
}

const styles = css.create({
  chip: {
    width: 'fit-content',
    paddingInline: 12,
    paddingBlock: 6,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: color.neutral20,
    borderRadius: 8,
    backgroundColor: color.neutral0,
    cursor: 'pointer',
    transition: 'border-color 0.15s',
    ':hover': {
      borderColor: color.neutral40,
    },
  },
  chipOpen: {
    borderColor: color.neutral40,
  },
  categoryContainerNoValue: {
    marginTop: 2,
  },
  valueContainer: {
    minWidth: 91,
    maxWidth: 210,
    textAlign: 'left',
  },
});
